// A small SVG line chart (no chart library): one 2px line per series over a shared x axis of
// periods (days, weeks, months or years), labelled y ticks and a legend in the bar chart's colours.
// Pointing at (or tabbing to) a period draws a crosshair and floats that period's figures over the
// plot beside it — the period on top, one row per series, biggest first — out of the flow, so the
// layout never moves; a visually hidden live region says the same in one sentence. The SVG is one
// labelled image to assistive technology, with the same numbers in a visually hidden table.
// Geometry: ui/chart-model.ts; styles: styles/ui.css.
import { useState } from "preact/hooks";
import { useWidth } from "./chart";
import { labelEvery, linePath, nearestIndex, niceScale, plotArea, pointXs, readoutRows, readoutSummary, seriesClass, yFor } from "./chart-model";

export interface LineSeries {
  id: string;
  label: string;
  /** One point per x, every series over the same xs in the same order. */
  points: readonly { x: string; y: number }[];
}

export interface LineChartProps {
  series: readonly LineSeries[];
  /** What y measures ("Tokens"), for the readout, the table header and the summary. */
  yLabel?: string | undefined;
  /** What one x is ("day", "week", "month", "year"), for the summary and the hints. */
  periodNoun?: string | undefined;
  /** One sentence naming what the chart shows, for screen readers; a summary of the series by default. */
  label?: string | undefined;
  class?: string | undefined;
  /** Plot height in px (the whole chart stays ≤ 260px). */
  height?: number | undefined;
  formatTick?: ((value: number) => string) | undefined;
  /** The x tick and readout text ("3 Sep 2026"); the x string itself by default. */
  formatX?: ((x: string) => string) | undefined;
  /** The short x tick under the axis ("3"); `formatX` by default. */
  formatXTick?: ((x: string) => string) | undefined;
  formatValue?: ((value: number) => string) | undefined;
}

const plain = (value: number) => value.toLocaleString("en-US");
const same = (x: string) => x;

/** Past this share of the plot width the readout sits on the guide's left, so it never overflows the right edge. */
const READOUT_FLIP_AT = 60;

export function LineChart({
  series,
  yLabel = "Value",
  periodNoun = "day",
  label,
  class: className,
  height = 220,
  formatTick = plain,
  formatX = same,
  formatXTick = formatX,
  formatValue = plain,
}: LineChartProps) {
  const { ref, width } = useWidth(640);
  const [active, setActive] = useState<number | null>(null);
  const xs = series[0]?.points.map((p) => p.x) ?? [];
  const totalHeight = Math.min(260, height);
  const scale = niceScale(Math.max(0, ...series.flatMap((s) => s.points.map((p) => p.y))), 4);
  const tickWidth = Math.max(36, ...scale.ticks.map((tick) => formatTick(tick).length * 7 + 8));
  const area = plotArea({ width, height: totalHeight, left: tickWidth, right: 8, top: 8, bottom: 24 });
  const positions = pointXs(xs.length, area);
  const every = labelEvery(xs.length, area.width);
  const summary = label ?? `${yLabel} per ${periodNoun}, ${series.length} ${series.length === 1 ? "line" : "lines"}: ${series.map((s) => s.label).join(", ")}.`;

  const onPointer = (event: PointerEvent) => {
    const svg = event.currentTarget as SVGSVGElement;
    const box = svg.getBoundingClientRect();
    setActive(nearestIndex(((event.clientX - box.left) / Math.max(1, box.width)) * width, positions));
  };
  const onKey = (event: KeyboardEvent) => {
    if (xs.length === 0) return;
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (event.key === "Home" || event.key === "End") setActive(event.key === "Home" ? 0 : xs.length - 1);
    else if (step) setActive((current) => Math.max(0, Math.min(xs.length - 1, (current ?? (step > 0 ? -1 : xs.length)) + step)));
    else return;
    event.preventDefault();
  };
  const activeX = active === null ? undefined : positions[active];
  const activeLabel = active === null ? null : formatX(xs[active] ?? "");
  const rows = active === null ? [] : readoutRows(series, active);
  // The guide's position as a share of the plot's rendered width, for the readout's `left`.
  const readoutX = activeX === undefined ? 0 : (activeX / Math.max(1, width)) * 100;

  return (
    <div class={className ? `chart ${className}` : "chart"}>
      <ul class="chart-legend" aria-hidden="true">
        {series.map((s, index) => (
          <li key={s.id}>
            <svg class="legend-swatch" viewBox="0 0 10 10">
              <rect class={seriesClass(s.id, index)} x="0" y="0" width="10" height="10" rx="2" />
            </svg>
            {s.label}
          </li>
        ))}
      </ul>
      <p class="chart-readout-hint" aria-hidden="true">
        Point at or tab to the chart and use the arrow keys for a {periodNoun}'s figures.
      </p>
      <p class="visually-hidden" aria-live="polite">
        {activeLabel === null ? "" : readoutSummary(activeLabel, rows, formatValue)}
      </p>
      {/* The focus target: arrow keys move between periods; the live region above announces each.
          The readout floats inside it (ui.css reads --readout-x), so nothing in the flow changes. */}
      <div ref={ref} class="chart-plot" tabIndex={0} onKeyDown={onKey} onBlur={() => setActive(null)} aria-label={`${summary} Arrow keys step through the ${periodNoun}s.`} role="group">
        {activeLabel !== null && (
          <div class={readoutX >= READOUT_FLIP_AT ? "chart-readout chart-readout-flip" : "chart-readout"} aria-hidden="true" style={`--readout-x: ${readoutX}%`}>
            <table class="chart-readout-table">
              <caption>{activeLabel}</caption>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td class="chart-readout-series">
                      <svg class="legend-swatch" viewBox="0 0 10 10">
                        <rect class={row.swatch} x="0" y="0" width="10" height="10" rx="2" />
                      </svg>
                      {row.label}
                    </td>
                    <td class="chart-readout-value">{formatValue(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <svg
          viewBox={`0 0 ${width} ${totalHeight}`}
          width={width}
          height={totalHeight}
          role="img"
          aria-label={summary}
          onPointerMove={onPointer}
          onPointerLeave={() => setActive(null)}
        >
          {scale.ticks.map((tick) => {
            const y = yFor(tick, scale, area);
            return (
              <g key={tick}>
                <line class={tick === 0 ? "chart-baseline" : "chart-grid-line"} x1={area.x} x2={area.x + area.width} y1={y} y2={y} />
                <text class="chart-tick" x={area.x - 8} y={y} text-anchor="end" dominant-baseline="middle">
                  {formatTick(tick)}
                </text>
              </g>
            );
          })}
          {xs.map((x, index) =>
            index % every === 0 ? (
              <text key={x} class="chart-label" x={positions[index]} y={area.y + area.height + 16} text-anchor="middle">
                {formatXTick(x)}
              </text>
            ) : null,
          )}
          {activeX !== undefined && <line class="chart-crosshair" x1={activeX} x2={activeX} y1={area.y} y2={area.y + area.height} />}
          {series.map((s, index) => (
            <path
              key={s.id}
              class={`chart-line ${seriesClass(s.id, index)}`}
              d={linePath(s.points.map((p, i) => ({ x: positions[i] ?? area.x, y: yFor(p.y, scale, area) })))}
            />
          ))}
          {activeX !== undefined &&
            series.map((s, index) => {
              const point = active === null ? undefined : s.points[active];
              return point ? <circle key={s.id} class={`chart-dot ${seriesClass(s.id, index)}`} cx={activeX} cy={yFor(point.y, scale, area)} r="4" /> : null;
            })}
        </svg>
      </div>
      <table class="visually-hidden">
        <caption>{summary}</caption>
        <thead>
          <tr>
            <th scope="col">{capitalize(periodNoun)}</th>
            {series.map((s) => (
              <th key={s.id} scope="col">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {xs.map((x, index) => (
            <tr key={x}>
              <th scope="row">{formatX(x)}</th>
              {series.map((s) => (
                <td key={s.id}>{formatValue(s.points[index]?.y ?? 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
